import { useHandler } from "@/hooks";
import { selectVersionInformationFromState } from "@/lib/hooks/useVersionInformation";
import { useMessageStore } from "@/state/stores";
import { useNavigate, useSearch } from "@tanstack/react-router";

export function useTabNavigation({
  chatId,
  versionNumber,
}: {
  chatId: string;
  versionNumber: number;
}) {
  const navigate = useNavigate();
  const tabMapping = {
    brainstorm: "spec",
    code: "code",
    preview: "preview",
  };

  const searchParams = useSearch({
    strict: false,
    select: (search) => search as { tab?: string; file?: string } | undefined,
  });

  const currentTab = searchParams?.tab ?? "spec";
  const computedActiveTab =
    Object.keys(tabMapping).find(
      (key) => tabMapping[key as keyof typeof tabMapping] === currentTab,
    ) || "brainstorm";

  const navigateTab = useHandler((tab: string) => {
    if (!chatId) {
      return;
    }

    switch (tab) {
      case "brainstorm":
        navigate({
          to: "/codegen/$chatId/attachments/$versionNumber",
          params: {
            chatId,
            versionNumber: `v${versionNumber ?? 1}`,
          },
          search: { tab: "spec" },
        });
        break;
      case "code": {
        // Performance tweak:
        // Access the store directly to get the current files
        const versionInformation = selectVersionInformationFromState(
          useMessageStore.getState(),
          versionNumber,
        );
        const currentFiles = versionInformation?.files ?? [];
        navigate({
          to: "/codegen/$chatId/attachments/$versionNumber",
          params: {
            chatId,
            versionNumber: `v${versionNumber ?? 1}`,
          },
          search: { tab: "code", file: currentFiles[0]?.filename },
        });
        break;
      }
      case "preview":
        navigate({
          to: "/codegen/$chatId/attachments/$versionNumber",
          params: {
            chatId,
            versionNumber: `v${versionNumber ?? 1}`,
          },
          search: { tab: "preview" },
        });
        break;
    }
  });

  return {
    navigateTab,
    computedActiveTab,
  };
}
